import { Box, Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FC, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  getJobCandidateInteraction,
  ICandidate,
  IJob,
  IJobCandidateInteractionResponse,
  loadCandidateByCandidateId,
  getAllInteractions,
  getJobsFromAdvancedQuery,
  JobAssignmentStatusFilter,
  getJobByJobCode,
  getSubmissionSettings,
  fetchCandiateFieldMapping,
  candiateDefaultFieldMapping,
} from 'wisestep-api';
import {
  IConversationState,
  ScrollableTabs,
  SubmissionTimeline,
  ICandidateAggregatedData,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  WsColors,
} from 'wisestep-base';

import UpdateStatusModal from '../../../components/UpdateStatusModal/UpdateStatusModal';
import { useWorkspaceContext } from '../../../context';
import StageStatusContextProvider from '../../../features/submissions/context/StageStatusContext';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { syncPostgreAndESCandidate } from '../../candidates/utils/CandidateHelper';
import { useJobScreenContext } from '../context/JobScreenContext';
import { dncScrollList, submissionScrollList } from '../utils/conversationScrollList';

import CandidateSmsLogs from './CandidateSmsLogs';
import CustomDNCMessage from './CustomDNCMessage';
import DataPrivacy from './DataPrivacy';
import EmailLogs from './EmailLogs';
import { EventsTab } from './EventsTab';
import InteractionTaskList from './InteractionTaskList';
import JobSelection from './JobSelection';
import SubmissionCandidateReview from './SubmissionCandidateReview';
import SubmissionHistory, { SwitchButton } from './SubmissionHistory';
import SubmissionWidgetLayout from './SubmissionWidgetLayout';

const workspaceIds: string[] = [];

interface ISubmissionWidgetUI {
  state: IConversationState;
  selectedJob: IJob | null;
  aggregatedData: ICandidateAggregatedData | null;
}

const SubmissionWidgetUI: FC<ISubmissionWidgetUI> = ({ state, selectedJob, aggregatedData }) => {
  const [planIndex, setPlanIndex] = useState<number>(4);
  const [activity, setActivity] = useState<IJobCandidateInteractionResponse[]>(
    aggregatedData?.recentInteractions ? aggregatedData.recentInteractions : []
  );
  const [activityHistory, setActivityHistory] = useState<IJobCandidateInteractionResponse | null>(
    null
  );
  const jobScreen = useJobScreenContext();
  const showJobScreen = jobScreen.showJobScreen;
  const [showJobSelectionScreen, setShowJobSelectionScreen] = useState<boolean>(false);
  const [profileInfo, setProfileInfo] = useState<ICandidate | null>(null);
  const [isStateInitialized, setIsStateInitialized] = useState(false);
  const [totalConversation, setTotalConversation] = useState<string | null>(null);
  const view = useConversationWidgetStore((state) => state.view);
  const setTaskFilterJobId = useConversationWidgetStore((state) => state.setTaskFilterJobId);
  const taskFilterJobId = useConversationWidgetStore((state) => state.taskFilterJobId);
  const searchQuery = useConversationWidgetStore((state) => state.searchQuery);
  const updateActivityConversation = useConversationWidgetStore(
    (state) => state.updateActivityConversation
  );
  const isDnc = state.candidate.isInDnc ?? false;
  const [tabIndex, setTabIndex] = useState<number>(!isDnc ? 0 : view === 'submission' ? 0 : 1);
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
  const closeUpdateStatusModal = () => setOpenStatusModal(false);
  const eraseData = useConversationWidgetStore((state) => state.eraseData);
  const [updateStatusCandidateInteraction, setUpdateStatusCandidateInteraction] =
    useState<ICandidate>(state.candidate);
  const [evalTemplateId, setEvalTemplateId] = useState(null);
  const [flag, setFlag] = useState();
  const workspace = useWorkspaceContext();

  const redirectToJob = async (jobCode: string) => {
    const response = await getJobsFromAdvancedQuery({
      jobCodes: [jobCode],
      page: 0,
      size: 2,
      sort: ['scoringData.score,desc'],
      searchAcross: ['MY_TEAMS', 'MY_PERSONAL_DATA'],
      jobAssignmentStatusFilter: JobAssignmentStatusFilter.Everything,
      jobStatus: 'All',
    });

    if (!isEmpty(response.data) && response.data[0]) {
      openUrlInNewTab(`/jobs-crm/${response.data[0].id}/false/0/pipeline/`);
    }
    if (isEmpty(response.data)) {
      const jobResponse = await getJobByJobCode(jobCode);
      openUrlInNewTab(`/jobs-crm/${jobResponse.data.id}/false/0/pipeline/`);
    }
  };

  const setIndex = useCallback(
    async (index: number) => {
      if (index === 0 && view === 'submission' && aggregatedData?.candidateEntity.candidateId) {
        const res = await loadCandidateByCandidateId(aggregatedData.candidateEntity.candidateId);

        res.data.passportNumber = isEmpty(res.data.passportNumber)
          ? aggregatedData.candidateEntity.passportNumber
          : res.data.passportNumber;

        res.data.passportType = isEmpty(res.data.passportType)
          ? aggregatedData.candidateEntity.passportType
          : res.data.passportType;

        res.data.dob = isEmpty(res.data.dob) ? aggregatedData.candidateEntity.dob : res.data.dob;

        res.data.gender = isEmpty(res.data.gender)
          ? aggregatedData.candidateEntity.gender
          : res.data.gender;

        res.data.age = isEmpty(res.data.age) ? aggregatedData.candidateEntity.age : res.data.age;

        res.data.postalAddress = isEmpty(res.data.postalAddress)
          ? aggregatedData.candidateEntity.postalAddress
          : res.data.postalAddress;

        res.data.ssnNumber = isEmpty(res.data.ssnNumber)
          ? aggregatedData.candidateEntity.ssnNumber
          : res.data.ssnNumber;

        res.data.panCard = isEmpty(res.data.panCard)
          ? aggregatedData.candidateEntity.panCard
          : res.data.panCard;

        res.data.aadharNumber = isEmpty(res.data.aadharNumber)
          ? aggregatedData.candidateEntity.aadharNumber
          : res.data.aadharNumber;
        syncPostgreAndESCandidate(res.data, state.candidate);
        setProfileInfo(res.data);
      } else if (index === 1 && aggregatedData?.candidateEntity?.candidateId) {
        const res = await loadCandidateByCandidateId(aggregatedData.candidateEntity.candidateId);

        res.data.passportNumber = isEmpty(res.data.passportNumber)
          ? aggregatedData.candidateEntity?.passportNumber
          : res.data.passportNumber;

        res.data.passportType = isEmpty(res.data.passportType)
          ? aggregatedData.candidateEntity?.passportType
          : res.data.passportType;

        res.data.dob = isEmpty(res.data.dob) ? aggregatedData.candidateEntity?.dob : res.data.dob;

        res.data.gender = isEmpty(res.data.gender)
          ? aggregatedData.candidateEntity.gender
          : res.data.gender;

        res.data.age = isEmpty(res.data.age) ? aggregatedData.candidateEntity?.age : res.data.age;

        res.data.postalAddress = isEmpty(res.data.postalAddress)
          ? aggregatedData.candidateEntity?.postalAddress
          : res.data.postalAddress;

        res.data.ssnNumber = isEmpty(res.data.ssnNumber)
          ? aggregatedData.candidateEntity?.ssnNumber
          : res.data.ssnNumber;

        res.data.panCard = isEmpty(res.data.panCard)
          ? aggregatedData.candidateEntity?.panCard
          : res.data.panCard;

        res.data.aadharNumber = isEmpty(res.data.aadharNumber)
          ? aggregatedData.candidateEntity?.aadharNumber
          : res.data.aadharNumber;
        syncPostgreAndESCandidate(res.data, state.candidate);
        setProfileInfo(res.data);
      } else if (index === 2) {
        setTaskFilterJobId(null);
      }
      setTabIndex(index);
    },
    [aggregatedData, setTaskFilterJobId, state.candidate, view]
  );

  useEffect(() => {
    if (view === 'submission') {
      isEmpty(profileInfo) && setIndex(0);
    }
  }, [profileInfo, setIndex, tabIndex, view]);

  useEffect(() => {
    if (aggregatedData && aggregatedData?.jobInteraction && isStateInitialized) {
      setActivity(aggregatedData?.recentInteractions ? aggregatedData.recentInteractions : []);
      setTotalConversation((aggregatedData?.mappings + aggregatedData?.submissions).toString());
    }
  }, [aggregatedData, isStateInitialized]);

  useEffect(() => {
    if (!isStateInitialized) {
      if (aggregatedData) {
        setActivity(aggregatedData?.recentInteractions ? aggregatedData.recentInteractions : []);
        setTotalConversation((aggregatedData?.mappings + aggregatedData?.submissions).toString());
      }
      setIsStateInitialized(true);
    }
  }, [aggregatedData, isStateInitialized, searchQuery]);

  useEffect(() => {
    showJobScreen != null && setShowJobSelectionScreen(showJobScreen);
  }, []);

  const onActivityUpdate = async (interactionId) => {
    if (interactionId) {
      const res = await getJobCandidateInteraction(interactionId);
      syncPostgreAndESCandidate(res.data, state.candidate);
      updateActivityConversation(res.data);
    }
  };

  const onSubmissionStatusUpdate = async (interactionId) => {
    if (interactionId) {
      const res = await getJobCandidateInteraction(interactionId);
      setUpdateStatusCandidateInteraction(res.data);
      setOpenStatusModal(true);
    }
  };

  const loadMoreActivity = async () => {
    if (aggregatedData?.candidateEntity.candidateId) {
      const res = await getAllInteractions(aggregatedData?.candidateEntity.candidateId);
      setActivity(res.data);
    }
  };

  const loadHistory = async (id: string) => {
    setTaskFilterJobId(null);
    const res = await getJobCandidateInteraction(id);
    setTaskFilterJobId(res.data.jobCode);
    setActivityHistory(res.data);
  };

  const getFieldSettings = async (isMapping: boolean) => {
    if (isMapping) {
      const req = await fetchCandiateFieldMapping();
      if (!isEmpty(req.data)) {
        return req.data;
      } else {
        const resetDefaultSettingsDetails = await candiateDefaultFieldMapping();
        return resetDefaultSettingsDetails.data;
      }
    }
    const req = await getSubmissionSettings();
    return req.data;
  };

  return (
    <>
      <Helmet>
        <title>{aggregatedData?.candidateEntity?.name ?? ''}</title>
      </Helmet>
      {isStateInitialized && (
        <SubmissionWidgetLayout
          state={state}
          selectedJob={selectedJob}
          aggregation={aggregatedData}
          update={false}
        >
          {showJobSelectionScreen ? (
            <JobSelection onBackButton={() => setShowJobSelectionScreen(false)} />
          ) : (
            <>
              <ScrollableTabs
                scrollList={
                  eraseData
                    ? dncScrollList()
                    : submissionScrollList(
                        activity.filter((candidate: IJobCandidateInteractionResponse) =>
                          candidate.candidateStatusTags.includes('submitted')
                        ).length ?? '0'
                      )
                }
                defaultValue={tabIndex}
                getTabIndex={(index: number) => setTabIndex(index)}
              />

              {tabIndex === 0 && !eraseData && (
                <>
                  {!isDnc ? (
                    <SubmissionHistory
                      isInDnc={isDnc}
                      totalConversation={totalConversation}
                      conversations={activity}
                      history={activityHistory}
                      onSubmissionStatusUpdate={onSubmissionStatusUpdate}
                      onActivityUpdate={onActivityUpdate}
                      onLoadMore={loadMoreActivity}
                      onClickOnJobCode={(jobCode: string) => redirectToJob(jobCode ?? '')}
                      loadHistory={loadHistory}
                      selectedJob={selectedJob}
                      currentJobCode={state.candidate.jobCode}
                      taskLisiting={
                        taskFilterJobId ? (
                          <InteractionTaskList state={state} hideEmptyScreen={true} />
                        ) : (
                          <></>
                        )
                      }
                    />
                  ) : (
                    <Grid item xs={9} m={'Auto'} px={5}>
                      <CustomDNCMessage
                        candidate={state.candidate}
                        onClick={() => setTabIndex(4)}
                      />
                    </Grid>
                  )}
                </>
              )}
              {tabIndex === 1 && !eraseData && (
                <>
                  {!isDnc ? (
                    <SubmissionTimeline
                      candidate={state.candidate}
                      getFieldSettings={getFieldSettings}
                      emailLogs={<EmailLogs candidateEmail={state.candidate.email} />}
                    />
                  ) : (
                    <Grid item xs={9} m={'Auto'} px={5}>
                      <CustomDNCMessage
                        candidate={state.candidate}
                        onClick={() => setTabIndex(4)}
                      />
                    </Grid>
                  )}
                </>
              )}
              {tabIndex === 2 && !eraseData && (
                <>
                  {!isDnc ? (
                    workspaceIds.includes(workspace.id as string) ? (
                      <Grid item xs={12} display="flex" justifyContent="center" pt={12}>
                        <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.SECONDARY}>
                          Coming Soon
                        </WsTypography>
                      </Grid>
                    ) : (
                      <EventsTab
                        setEvalTemplateId={setEvalTemplateId}
                        setTabIndex={setTabIndex}
                        candidate={updateStatusCandidateInteraction}
                        setFlag={setFlag}
                        state={state}
                      />
                    )
                  ) : (
                    <Grid item xs={9} m={'Auto'} px={5}>
                      <CustomDNCMessage
                        candidate={state.candidate}
                        onClick={() => setTabIndex(4)}
                      />
                    </Grid>
                  )}
                </>
              )}
              {tabIndex === 3 && !eraseData && (
                <>
                  <Box textAlign="center" mt={2}>
                    <SwitchButton
                      noWidth
                      variant="outlined"
                      bgColor={planIndex === 0 ? WsColors.TheBlues100 : WsColors.White}
                      onClick={() => setPlanIndex(0)}
                    >
                      Call
                    </SwitchButton>
                    <SwitchButton
                      noWidth
                      variant="outlined"
                      bgColor={planIndex === 1 ? WsColors.TheBlues100 : WsColors.White}
                      onClick={() => setPlanIndex(1)}
                    >
                      SMS
                    </SwitchButton>
                    <SwitchButton
                      noWidth
                      variant="outlined"
                      bgColor={planIndex === 2 ? WsColors.TheBlues100 : WsColors.White}
                      onClick={() => setPlanIndex(2)}
                    >
                      Email
                    </SwitchButton>
                  </Box>
                  {planIndex == 0 && (
                    <Box textAlign="center" mt={25}>
                      <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.SECONDARY}>
                        Coming Soon
                      </WsTypography>
                    </Box>
                  )}
                  {planIndex == 1 && aggregatedData && (
                    <CandidateSmsLogs candidate={profileInfo} aggregation={aggregatedData} />
                  )}
                  {planIndex == 2 && (
                    <>
                      {!isDnc ? (
                        <EmailLogs candidateEmail={state.candidate.email} />
                      ) : (
                        <Grid item xs={9} m={'Auto'} px={5}>
                          <CustomDNCMessage
                            candidate={state.candidate}
                            onClick={() => setTabIndex(4)}
                          />
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )}
              {tabIndex === 5 && !eraseData && (
                <DataPrivacy
                  email={state.candidate.email}
                  candidateName={state.candidate.name}
                  isInDnc={isDnc}
                  dncDetails={state.candidate.dncDetails}
                />
              )}
            </>
          )}
          {tabIndex === 4 && !eraseData && (
            <>
              {!isDnc ? (
                <SubmissionCandidateReview
                  candidiateId={state.candidate.id}
                  candidiateData={state.candidate}
                  currentJobCode={state.candidate.jobCode}
                  templateId={evalTemplateId}
                  flag={flag}
                />
              ) : (
                <Grid item xs={9} m={'Auto'} px={5}>
                  <CustomDNCMessage candidate={state.candidate} onClick={() => setTabIndex(4)} />
                </Grid>
              )}
            </>
          )}
        </SubmissionWidgetLayout>
      )}
      {openStatusModal && (
        <StageStatusContextProvider>
          <UpdateStatusModal
            candidate={updateStatusCandidateInteraction}
            openStatusModal={openStatusModal}
            onClose={closeUpdateStatusModal}
          />
        </StageStatusContextProvider>
      )}
    </>
  );
};

export default SubmissionWidgetUI;
